import { useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import { StreamsContext } from '../contexts/StreamsContext';
import { AuthContext } from '../contexts/AuthContext';
import { useBarsFetcher } from './useTradeStationQueries';
import { mergeBars as mergeChartBars } from '../utils/chartBarMerge';

/**
 * Hook for charts to read bar data from context
 * Stream management is handled by BarStreamManager components
 */
export const useBarStreamData = (ticker, interval, unit, barsback = 100, sessionTemplate = 'Default') => {
    const { barsByKey, setBarsByKey, barsStreamMetaByKey } = useContext(StreamsContext);
    const { user } = useContext(AuthContext);
    const fetchBars = useBarsFetcher();
    const isLoadingMoreRef = useRef(false);
    const barsByKeyRef = useRef(barsByKey);
    barsByKeyRef.current = barsByKey;

    const barsKey = useMemo(() => {
        if (!ticker || !interval || !unit) return null;
        return [ticker, interval, unit, sessionTemplate || 'Default'].join('|');
    }, [ticker, interval, unit, sessionTemplate]);

    const bars = useMemo(() => {
        if (!barsKey) return [];
        const barsArray = barsByKey[barsKey] || [];
        return barsArray;
    }, [barsByKey, barsKey]);

    const meta = barsKey ? barsStreamMetaByKey[barsKey] : undefined;

    /** Initial load finished. Live ticks must not count — CT subscribe backfill is only 2 bars. */
    const historyResolved =
        meta?.status === 'ready' ||
        meta?.status === 'error';

    const barsLoadError =
        meta?.status === 'error' ? (meta.message || 'Chart data unavailable.') : null;

    const mergeBars = useCallback((existingBars, incomingBars) => {
        return mergeChartBars(existingBars, incomingBars, { interval, unit });
    }, [interval, unit]);

    const loadMoreHistoricalBars = useCallback(async (numBars = 500) => {
        if (isLoadingMoreRef.current || !barsKey || !user?.token || !ticker || !interval || !unit) {
            return false;
        }

        try {
            isLoadingMoreRef.current = true;

            const currentBars = barsByKeyRef.current[barsKey] || [];
            if (currentBars.length === 0) {
                isLoadingMoreRef.current = false;
                return false;
            }

            const earliestBar = currentBars[0];
            const lastdate = earliestBar?.TimeStamp
                ? new Date(new Date(earliestBar.TimeStamp).getTime() - 1000).toISOString()
                : undefined;

            const res = await fetchBars({
                ticker,
                interval,
                unit,
                barsback: numBars,
                sessionTemplate: sessionTemplate || 'Default',
                lastdate,
            });

            const newBars = (res?.Bars && Array.isArray(res.Bars)) ? res.Bars : [];
            if (newBars.length === 0) {
                isLoadingMoreRef.current = false;
                return false;
            }

            let grew = false;
            setBarsByKey(prev => {
                const before = prev[barsKey] || [];
                const merged = mergeBars(before, newBars);
                grew = merged.length > before.length;
                if (!grew) return prev;
                const next = {
                    ...prev,
                    [barsKey]: merged
                };
                // Eagerly update so sequential fill pages don't reuse a stale earliest bar.
                barsByKeyRef.current = next;
                return next;
            });

            isLoadingMoreRef.current = false;
            return grew;
        } catch (e) {
            console.error(`[useBarStreamData ${barsKey}] Load error:`, e);
            isLoadingMoreRef.current = false;
            return false;
        }
    }, [barsKey, user?.token, ticker, interval, unit, sessionTemplate, fetchBars, mergeBars, setBarsByKey]);

    return {
        bars,
        historyLoaded: historyResolved,
        barsLoadError,
        loadMoreHistoricalBars,
        barsKey
    };
};
